import { differenceInDays, format, formatDistance, startOfDay } from "date-fns";
import { DateTime } from "luxon";
export const REPORT_TIMEZONE = "Australia/Sydney";
export const getOpsTimezone = () => {
    return "Australia/Sydney";
};
/*
 * checks date is a valid yyyy-MM-dd iso calendar date string
 */
export function validateIsoCalendarDate(dateString) {
    const date = DateTime.fromISO(dateString, { zone: "utc" });
    if (!date.isValid) {
        return {
            valid: false,
            reason: `invalid calendar date: "${dateString}". ${date.invalidReason}: ${date.invalidExplanation}`,
        };
    }
    // fromIso takes in a variety of ISO formats (not just calendar date) - check that toISODate() matches the input
    const formatted = date.toISODate();
    if (formatted !== dateString) {
        return {
            valid: false,
            reason: `invalid calendar date: "${dateString}"`,
        };
    }
    return { valid: true };
}
/*
 * be careful before using these; often you'll want to use a different timezone such as organisation, report or utc
 *
 * these three are using local timezone (user or server, depending where run) for now (for determining day boundaries etc)
 * - todo: check whether server matches (for SSR)
 * - todo: get timezone from org details (or alternatively business entity) and use that
 */
export function localDateToLocalDayString(localDate) {
    return format(localDate, "yyyy-MM-dd");
}
/**
 * Formats a date into a format like: "1 Jan 2022"
 */
export function formatLocalDateShortDay(localDate) {
    return localDate.toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}
export function formatLocalDateTime(localDate) {
    return localDate.toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
    });
}
export function formatLocalDateComponents(localDate) {
    // e.g. "1 Jan 2022"
    const shortDate = formatLocalDateShortDay(localDate);
    // e.g. "1:23 pm"
    const shortTime = localDate.toLocaleTimeString("en-AU", {
        hour: "numeric",
        minute: "numeric",
    });
    // e.g. "01 July"
    const dayMonth = localDate.toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "short",
    });
    // e.g. "Australia/Sydney"
    const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return {
        shortDate,
        shortTime,
        timezoneName,
        dayMonth,
        datetimeTz: `${shortDate} ${shortTime} (${timezoneName})`,
        datetime: `${shortDate} ${shortTime}`,
    };
}
export function formatDateComponentsInTimeZone({ date, timeZone, }) {
    // throws if provided timeZone is not valid
    Intl.DateTimeFormat(undefined, { timeZone });
    // Format short date (e.g., "1 Jan 2022") in the specified timezone
    const shortDate = new Intl.DateTimeFormat("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone,
    }).format(date);
    // Format short time (e.g., "1:23 pm") in the specified timezone
    const shortTime = new Intl.DateTimeFormat("en-AU", {
        hour: "numeric",
        minute: "numeric",
        timeZone,
    }).format(date);
    // Format full time including seconds (e.g., "1:23:59 pm") in the specified timezone
    const fullTime = new Intl.DateTimeFormat("en-AU", {
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        timeZone,
    }).format(date);
    return {
        shortDate,
        shortTime,
        fullTime,
        timeZone,
    };
}
// this doesn't really support multi timezone yet
// - assumes dates are created in the same timezone as they are viewed (e.g. both Australia/Sydney)
export const getDateDelta = (date) => {
    const startOfTargetDay = startOfDay(date);
    const startOfToday = startOfDay(new Date());
    const deltaDays = differenceInDays(startOfTargetDay, startOfToday);
    if (deltaDays < -1) {
        return formatDistance(startOfTargetDay, startOfToday, { addSuffix: true });
    }
    if (deltaDays < 0) {
        return "yesterday";
    }
    if (deltaDays < 1) {
        return "today";
    }
    if (deltaDays < 2) {
        return "tomorrow";
    }
    return formatDistance(startOfTargetDay, startOfToday, { addSuffix: true });
};
// - during client-side rendering & logic this will return the user's timezone
// - during server-side rendering & logic this will return the server's timezone
// - if you need the user's timezone, use useUserTimezone instead
export const getLocalTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
// refer getLocalTimezone comments
export const getLocalTimezoneDateTimeString = ({ at, }) => {
    const localTimezone = getLocalTimezone();
    return DateTime.fromJSDate(at)
        .setZone(localTimezone)
        .toFormat("yyyy-MM-dd'T'HH:mm");
};
export const getLocalTimezoneDateString = ({ at }) => {
    const localTimezone = getLocalTimezone();
    return DateTime.fromJSDate(at).setZone(localTimezone).toFormat("yyyy-MM-dd");
};
export const getLocalTimezoneTimeString = ({ at }) => {
    const localTimezone = getLocalTimezone();
    return DateTime.fromJSDate(at).setZone(localTimezone).toFormat("HH:mm:ss");
};
/*
 * Mental model:
 * - travel to given point in time (e.g. current time)
 * - call someone in the target timezone and ask what day it is
 * - ask them when their day started, and to answer as a (timezone-independent) Date object
 *
 * @param at - the point in time to start from
 * @param timezone - the timezone to use for the day boundary
 */
export const getStartOfTimezoneDay = ({ at = new Date(), timezone, }) => {
    return DateTime.fromJSDate(at).setZone(timezone).startOf("day").toJSDate();
};
export function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
export const useUserTimezone = () => {
    return getLocalTimezone();
};
export const useUserTimezoneDateTimeString = ({ at, }) => {
    return getLocalTimezoneDateTimeString({ at });
};
export function now() {
    return DateTime.local();
}
export function isWeekday(dt) {
    return dt.weekday <= 5;
}
export function nextWeekday(dt) {
    let next = dt.plus({ days: 1 });
    while (!isWeekday(next)) {
        next = next.plus({ days: 1 });
    }
    return next;
}
